import type { CancelToken } from 'axios';
import { get } from 'lodash-es';

import API from 'apis/API';
import type { CompanyUserStatus } from 'apis/CompanyAPI';
import type { ExpenseType } from 'apis/ExpenseAPI/models';
import type { ReportRefundStage, ReportType } from 'apis/models/report';
import type { ReportRefundStage as ReportRefundStageKeys, ReportStage as ReportStageKeys } from 'apis/models/report';
import type { ApproverFlowPolicyRole } from 'apis/PolicyApprovalLineAPI';
import type { ReportSubmitType } from 'apis/PolicyMembersAPI';
import type { SpenditError, SpenditErrorResponse } from 'apis/shared';
import type {
  Attachment,
  Category,
  CustomField,
  Expense,
  ExportOption,
  NextAction,
  PaginationParam,
  ReportCustomFieldType,
} from 'apis/shared/types';
import { HttpQueryUtil } from 'components/utility/http_query_util';
import type LabelValue from 'types/labelValue';
import type { DecimalNumberString } from 'utils/CurrencyUtil/consts';
import type { ISO8601DateFormatString } from 'utils/Date';
import { isAWSSlowDownErrorResponse } from 'utils/ErrorHandler';

import type { ReportHistory } from '../ReportHistoryAPI/types';
import type {
  RecentWatcher,
  ReportBulkTransferAndRefundResult,
  ReportWatcher as ReportWatcherV2,
  ReportWatcherType,
  WatchAddableGroup,
  WatchAddableUser,
} from './models';

/**
 * @deprecated apis > models > report > ReportStage로 이관
 */
export type StageValue = 'ready' | 'sent' | 'refund_completed' | 'approved';

export enum APPROVER_AUTHORITY {
  REFUND_APPROVER = 'refund_approver',
  FINAL_APPROVER = 'final_approver',
  DEFAULT_APPROVER = 'default_approver',
  REVIEWER = 'reviewer',
}

type ApproverSlotRule = 'single' | 'and' | 'or';

type ApproverSeatState = 'waiting' | 'pending' | 'approved' | 'skipped' | 'passed' | 'vacated' | 'excluded';

type ApproverHolderState = 'left' | 'replaced';

export interface ApproverInfo {
  authority: string;
  current: boolean;
  email: string;
  id: number;
  name: string;
  slot_position?: number | null;
  slot_rule?: ApproverSlotRule | null;
  seat_state: ApproverSeatState;
  holder_state?: ApproverHolderState;
  replaced_from?: string | null;
}

export interface ReportViolation {
  count: number;
  hide: boolean;
  label: string;
}

export interface ExchangeRate {
  price: string;
  use_custom_rate: boolean;
}

export type ReportApprover = {
  id: number;
  email: string;
  name: string;
  company_name: string;
  thumbnail_url: string | null;
  role: PolicyMemberRole;
  status: CompanyUserStatus;
} & LabelValue<number>;

type ReportUser = {
  id: number;
  name: string;
  email: string;
  status?: CompanyUserStatus;
};

type ReportApproverMember = {
  id: number;
  email: string;
  name: string;
  label: string;
  value: number;
  role: PolicyMemberRole;
  thumbnail_url: string | null;
  user_id: number;
};

export type ReportDetailEditor = {
  templates: EditorTemplate[];
  selected_template: {
    id: number;
    title: string;
    is_default: boolean;
    content: {
      download_url: string;
      is_upload_complete: boolean;
    };
  };
  content: {
    download_url: string | null;
    is_upload_complete: boolean;
  };
};

export type EditorTemplate = {
  id: number;
  title: string;
  is_default: boolean;
  content: {
    download_url: string;
    is_upload_complete: boolean;
  };
};

type ReportTotalPriceByCurrency = {
  currency: string;
  currency_symbol: string;
  total_price: string;
};

export type PostWriterDesignationPermission = 'editable' | 'read_only' | 'none';
export type PlanBudgetItemModificationPermission = 'editable' | 'read_only' | 'none';

/**
 * GET /web/report/:id — `report` 필드 응답 모델
 * @see getReport
 */
export interface Report {
  approver: ReportApprover;
  approver_list: ReportApproverMember[];
  approvers_info: ApproverInfo[];
  completed: boolean;
  currency: string;
  custom_fields: CustomField[];
  edit_exchange_rate: boolean;
  editable: string[];
  editor: ReportDetailEditor | null;
  ends_at: string;
  exchange_rates: { [propName: string]: ExchangeRate };
  final_approver: ReportApproverMember | null;
  is_final_approver: boolean;
  first_sent_at: string | null;
  ghost_id: number | null;
  histories: ReportHistory[];
  id: number;
  latest_approved_at: string | null;
  latest_denied_at: string | null;
  latest_sent_at: string | null;
  next_action: NextAction[];
  not_purchased_count: number;
  period_type: boolean;
  plan_info: { limitation: number; exchanged_at?: string } | null;
  planning: boolean;
  policy: { id: number; label: string; report_policy: { show_exceed_amount: boolean } };
  refund_stage: ReportRefundStage;
  removable: boolean;
  report_type: ReportTypeLabel;
  restrict_submission: boolean;
  sender_info: string;
  stage: { label: string; value: StageValue };
  stage_str: string;
  starts_at: string;
  submit_type: string;
  title: string;
  total_amount: string;
  total_refund_amount: string;
  user: ReportUser;
  user_id: number;
  violations: ReportViolation[];
  policy_approval_line_info: { name: string; updated: boolean } | null;
  exceed_amount: string | null;
  total_price_by_currency: ReportTotalPriceByCurrency[];
  exported: ExportType[];
  fail_exported: ExportType[];
  available_actions: ReportAvailableAction[];
  finalized_at?: string;
  report_warnings?: ('invalid_approval_line' | 'invalid_no_plan_item')[];
  plan_closed: boolean | null;
  post_plan_reports: PlanPostReport[] | null;
  pre_plan_report: {
    id: number;
    title: string;
    currency: string;
    submitted_at: string;
    stage: Exclude<ReportStageKeys, null>;
    refund_stage: Exclude<ReportRefundStageKeys, null>;
    user: {
      id: number;
      name: string;
    };
    total_amount: number;
    completed: boolean;
    plan_info: {
      limitation: number;
    };
  } | null;
  is_designated_to_me: boolean; // 현재 사용자가 이 보고서의 사후 작성자로 지정되어 있는지
  can_create_post_plan: boolean; // 사후 보고서 생성 버튼 노출 여부
  can_close_plan: boolean; // 플랜 마감 버튼 노출 여부
  can_modify_plan_budget: boolean; // 플랜 예산 수정 버튼 노출 여부
  plan_budget_item_modification_permission: PlanBudgetItemModificationPermission;
  can_view_other_post_plans: boolean; // 타인 사후 보고서 / 플랜 예산 현황 조회 권한
  can_view_post_writers: boolean; // 사용자 지정 버튼(사후 작성자 목록 조회 팝업)의 활성 여부
  post_writer_designation_permission: PostWriterDesignationPermission; // 사용자 지정 드로워 편집 상태 3-state enum (PQ-2812)
  approval_line_options: ReportApprovalLineOptions | null; // 사후 보고서 전용. 일반/사전 보고서는 null
  approval_adjustment_needed: boolean;
}

export type ReportApprovalLineOptions = {
  allows_pre_plan_source: boolean;
  force_pre_plan_source: boolean;
  submitter_in_pre_plan_line: boolean;
  own_line_equals_pre_plan: boolean;
  submitter_has_own_line: boolean;
  pre_plan_preview: ReportApprovalLinePreviewItem[];
};

export type ReportApprovalLinePreviewItem = {
  id: number;
  name: string;
  email: string;
  thumbnail_url: string | null;
  step: number;
  role: 'approver' | 'final_approver';
};

export type ReportAvailableAction = 'remove' | 'share' | 'export' | 'duplicate' | 'print';

type ReportTypeLabel = LabelValue<Exclude<ReportType, null>>;

export interface ReportStage {
  label: string;
  value: string;
}

export type DenyReceiver = {
  authority?: APPROVER_AUTHORITY | null;
  email: string;
  id: number;
  label: string;
  name: string;
  owner: boolean;
  role: string;
  thumbnail_url: string | null;
  report_approver_id?: number | null;
  user_id: number;
  user_status: CompanyUserStatus;
  value: number;
};

export type RelatedReportDataMeta = {
  id: number;
  title: string;
  sent_at?: ISO8601DateFormatString;
  currency: string;
  total_calculated_price: DecimalNumberString;
  user: {
    id: number;
    name: string;
  };
  policy: {
    id: number;
    name: string;
  };
};

type ReportNote = {
  icon_url?: string;
  color: string;
  value: string;
  text: string;
};

type ReportMetaPolicy = {
  id: number;
  label: string;
  value: number;
};

export interface ReportMeta {
  attachments: Attachment[];
  categories: Category[];
  currency: string;
  currencies: LabelValue<string>[];
  check_types: LabelValue<string>[];
  deny_receivers: DenyReceiver[];
  export_options: ExportOption[];
  in_policy: boolean;
  locked: string;
  notes: ReportNote[];
  policies: ReportMetaPolicy[];
  report_types: ReportTypeLabel[];
  users_with_visibility: string[];
  related_report_data: RelatedReportDataMeta[];
}

export interface ReportComment {
  description: string;
  history_type: string;
  id: number;
  thumbnail_url: string;
  updated_at: string;
  user_id: number;
  user_name: string;
}

export interface ReportExpenseGroupReportPlanItem {
  id: number;
  currency: string;
  plan_name: string;
  check_type: string;
  name: string | null;
  limitation_info: {
    total_used_amount: string;
    expense_count_str: string;
    expense_count: number;
    limitation: string;
    total_submitted_amount: string;
    total_pending_amount: string;
    total_listed_amount: string;
  };
  descriptions: {
    label: string;
    value: string;
    app_only?: boolean;
  }[];
  is_custom: boolean;
  category_id: number;
}

export type PlanPostReport = {
  id: number;
  title: string;
  stage: Exclude<ReportStageKeys, null>;
  refund_stage: Exclude<ReportRefundStageKeys, null>;
  user: {
    id: number;
    name: string;
  };
  total_amount: string;
  total_amount_by_category: string;
  sent_at: string;
  created_at: string;
  currency: string;
  completed: boolean;
};

export interface ReportExpenseGroup {
  expenses: Expense[];
  label: string;
  amount: string;
  query: string;
  page: number;
  refund_amount: number;
  meta: {
    expense_ids: number[];
    page_count: number;
  };
  report_plan_item: ReportExpenseGroupReportPlanItem;
  post_plan_reports: PlanPostReport[];
}

export interface AttendeeStats {
  0: { id: number; name: string }[];
  1: {
    price: string;
    attendee_id: number;
    attendee_name: string;
  }[][];
}

export type PostSearchReportsParams = {
  filter: {
    title_or_name: string;
    planning: boolean;
    for_expense: boolean;
    for_expense_expense_type?: ExpenseType;
    report_types?: ReportType[];
  };
  policy_id: number;
  user_id: number;
};

type PostSearchReportsResponse = {
  reports: SearchReport[];
};

/**
 * post /web/search_reports
 */
export const postSearchReports = async (params: PostSearchReportsParams) => {
  const res = await API.post<PostSearchReportsResponse>('/web/search_reports', params);

  return res.data;
};

/**
 * GET /web/report/:id
 * @param id report ID
 * @param param
 */
export const getReport = async (id: string, includeExpense: boolean) => {
  const res = await API.get<{
    attendee_stats: AttendeeStats;
    expense_meta: ReportExpenseMeta;
    meta: ReportMeta;
    report: Report;
  }>(`/web/report/${id}`, {
    params: {
      include_expenses: includeExpense,
    },
  });

  return res.data;
};

export interface PagedReportsResponseReport {
  approver: ReportApprover | null;
  completed: boolean;
  csv_url: string | null;
  currency: string;
  erp_exported: boolean;
  erp_icons: { icon_url: string }[];
  export_icons: { icon_url: string }[];
  exported: string[];
  id: number;
  latest_approved_at: boolean;
  period: string | null;
  plan_status: string;
  planning: boolean;
  policy_approval_line_info: {
    id: number;
    name: string;
    report_type: string;
    limit_type: string;
    completion: boolean;
  };
  policy_approval_line_name: string | null;
  policy_id: number;
  policy_name: string;
  refund_stage: string;
  removable: boolean;
  report_type: ReportTypeLabel;
  sent_at: string;
  stage: ReportStage;
  stage_str: string;
  status: string | null;
  title: string;
  todo: string;
  total_amount: number;
  user_id: number;
  user: {
    label: string;
    value: number;
    id: number;
    email: string;
    name: string;
    company_name: string;
    thumbnail_url: string;
    status: string;
    accessible?: boolean;
  };
  is_watching_report: boolean;
  related_expenses_count: number;
  is_designated_to_me: boolean; // 현재 사용자가 이 보고서의 사후 작성자로 지정되어 있는지
  approval_adjustment: {
    needed_at: string;
    status: 'stalled' | 'upcoming';
    vacated_user_names: string[];
    steps_until_vacancy: number | null;
  } | null;
}

interface PagedReportsResponse {
  meta: {
    currency: string;
    current_page: number;
    is_first_page: boolean;
    is_last_page: boolean;
    limit_value: number;
    next_page: number;
    page_amount: string;
    page_count: number;
    prev_page: boolean | null;
    total_count: number;
  };
  reports: PagedReportsResponseReport[];
}

interface PagedReportsParams {
  page?: number;
  count_per_page?: number;
  filter: string;
  order?: string;
}

export interface ReportsMeta {
  has_approval_authority: boolean;
  has_transfer_and_refund_authority: boolean;
  bulk_approvable: boolean;
  export_options: {
    ile_type: string;
    format: string;
    group: string;
    icon_url: string;
    label: string;
    tax_refund?: boolean;
  }[];
  locked: boolean;
  policies_for_filter: {
    label: string;
    value: number | null;
    budget_policy?: {
      info_showable: boolean;
      over_price_limitable: boolean;
      member_visibility_limitable: boolean;
    };
    disabled?: boolean;
    edit_distance?: boolean;
    id?: number;
    name?: string;
    open_daily_wage?: boolean;
    open_time?: boolean;
    personal?: boolean;
    policy_type?: string;
    report_auto_title?: boolean;
  };
  report_schedule: string;
  report_statuses: number[];
  report_types_for_filter: string[];
  report_types: {
    expense: string[];
    general_approval: string[];
  };
  stages: {
    label: string;
    value: string;
  }[];
}

interface ReportsMetaResponse {
  meta: ReportsMeta;
}

/**
 * Get /web/reports
 * @param params
 */
export const getPagedReports = async (params: PagedReportsParams, cancelToken?: CancelToken) => {
  const res = await API.get<PagedReportsResponse>('/web/reports', { params, cancelToken });
  return res.data;
};

export const getReportsMeta = async () => {
  const res = await API.post<ReportsMetaResponse>('/web/reports/meta');
  return res.data;
};

export type PostReportParams = {
  report: string;
  filter?: string;
};

type PostReportResponse = {
  report: PagedReportsResponseReport;
};

/**
 * POST /web/report
 */
export const postReport = async (params: PostReportParams) => {
  const res = await API.post<PostReportResponse>('/web/report', params);

  return res.data;
};

type DeleteReportParams = {
  report_ids: number[];
  policy_id?: number;
  filter?: string;
  checked_all?: boolean;
};

type DeleteReportResponse = {
  ids: number[];
};

/**
 * DELETE /web/report
 */
export const deleteReport = async (params: DeleteReportParams) => {
  const res = await API.delete<DeleteReportResponse>('/web/report', {
    data: params,
  });

  return res.data;
};

const isERPFormat = (format: string) => {
  const erpFormats = ['icube', 'iu', 'sap'];
  return erpFormats.indexOf(format) > -1;
};

const getExportEndpointType = (format: string, fileType: string) => {
  if (isERPFormat(format) && fileType === 'erp_export') {
    return 'erp_download';
  }
  return 'file_download';
};

/**
 * POST /web/report/{:exportType}
 *
 * @description /web/report/erp_download | /web/report/file_download
 */
export const exportReport = async (params: ExportReportParam, cancelToken?: CancelToken) => {
  const res = await API.post(
    `/web/report/${getExportEndpointType(params.format, get(params, 'options.file_type', 'erp_export'))}`,
    {
      policy_id: params.policyId,
      format: params.format,
      id: params.id,
      editor_content: params.editor_content,
      options: JSON.stringify(params.options),
    },
    { timeout: Number(process.env.REACT_APP_DOWNLOAD_API_TIMEOUT), cancelToken }
  );

  return res.data;
};

/**
 * POST /web/reports/{:exportType}
 *
 * @description /web/reports/erp_download | /web/reports/file_download
 */
export const exportReports = async (params: ExportReportsParam) => {
  const res = await API.post(
    `/web/reports/${getExportEndpointType(params.format, get(params, 'options.file_type', 'erp_export'))}`,
    {
      report_ids: params.reportIds,
      checked_all: params.checkedAll,
      format: params.format,
      filter: params.filter,
      options: params.options,
    },
    { timeout: Number(process.env.REACT_APP_DOWNLOAD_API_TIMEOUT) }
  );

  return res.data;
};

type PostReportShareParams = {
  id: number;
  receiver: string;
  cc: string;
  content: string;
  fileType: string;
  divide: string;
  editorContent: string;
};

/**
 * POST /web/report/share
 */
export const postReportShare = async (params: PostReportShareParams) => {
  const res = await API.post(
    '/web/report/share',
    HttpQueryUtil.FormData({
      id: params.id,
      email_params: JSON.stringify({
        receiver: params.receiver,
        cc: params.cc,
        content: params.content,
        file_type: params.fileType,
        divide: params.divide,
        editor_content: params.editorContent,
      }),
    })
  );

  return res.data;
};

/**
 * get /web/report/comments
 * @param params
 */
export const getComments = async (id: number, page: number, countPerPage = 10) => {
  const res = await API.get<{
    comments: ReportComment[];
    page_count: number;
    total_count: number;
  }>('/web/report/comments', { params: { id, page, count_per_page: countPerPage } });

  return res.data;
};

/**
 * POST /web/report/comment
 * @param id report ID
 */
export const saveComment = async (id: number, comment: string) => {
  const res = await API.post('/web/report/comment', { report: JSON.stringify({ comment }), id });
  return res.data;
};

export const deleteComment = async (historyId: number, reportId: number) => {
  const res = await API.delete('/web/report/comment', {
    data: {
      report_history_id: historyId,
      id: reportId,
    },
  });
  return res.data;
};

/**
 * PUT /web/report/comment
 */
export const updateComment = async (reportId: number, historyId: number, description: string) => {
  const res = await API.put('/web/report/comment', {
    description,
    report_history_id: historyId,
    id: reportId,
  });
  return res.data;
};

type PostReportExpensesParams = {
  id: number;
  listAll: boolean;
  pagination: PaginationParam;
  target: string | null;
  targetOption?: {
    categoryId?: string;
    tagIds?: number[];
    paymentId?: number;
    paymentType?: string;
    reportPlanItemId?: number;
  };
};

type PostReportExpenseResponse = { expense_list: ReportExpenseGroup[] } | ReportExpenseGroup;

/**
 * POST /web/report/expenses
 */
export const postReportExpenses = async (params: PostReportExpensesParams) => {
  const res = await API.post<PostReportExpenseResponse>('/web/report/expenses', {
    id: params.id,
    list_all: params.listAll,
    page: params.pagination.page,
    count_per_page: params.pagination.countPerPage,
    target: params.target,
    category_id: params.targetOption?.categoryId,
    tag_ids: params.targetOption?.tagIds,
    payment_id: params.targetOption?.paymentId,
    payment_type: params.targetOption?.paymentType,
    report_plan_item_id: params.targetOption?.reportPlanItemId,
  });

  return res.data;
};

/**
 * PUT /web/report/plan_clode
 */

export type PutReportPlanCloseParams =
  | { reportId: number; cancel?: never; checkReports?: never }
  | { reportId: number; cancel: true; checkReports?: never }
  | { reportId: number; cancel?: never; checkReports: true };

type PutReportPlanCloseResponse = {
  report: {
    post_plan_reports: PlanPostReport[];
  };
};

export const putReportPlanClose = async (params: PutReportPlanCloseParams) => {
  const reportId = params.reportId;
  const cancel = 'cancel' in params ? params.cancel : undefined;
  const checkReports = 'checkReports' in params ? params.checkReports : undefined;

  const res = await API.put<PutReportPlanCloseResponse>('/web/report/plan_close', {
    id: reportId,
    cancel,
    check_only: checkReports,
  });

  return res.data;
};

/**
 * DELETE /web/report_plan_item
 */

export const deleteReportPlanItem = async (reportId: number, planId: number) => {
  const res = await API.delete('/web/report_plan_item', {
    data: {
      id: planId,
      report_id: reportId,
    },
  });
  return res.data;
};

export type PostActionBody = { id: number; skip_validation?: boolean; report?: string } & Record<string, unknown>;

export const postAction = async (actionType: ReportActionType, body: PostActionBody) => {
  const res = await API.post(`/web/report/${actionType}`, HttpQueryUtil.FormData(body));

  return res.data;
};

export const bulkActionUsers = async (
  stage: 'refund' | 'cancel_approve',
  policyId: number,
  isCheckedAll: boolean,
  userIds: number[]
) => {
  const res = await API.post(
    `/web/reports/bulk_action_users`,
    HttpQueryUtil.FormData({
      stage,
      policy_id: policyId,
      checked_all: isCheckedAll,
      user_ids: [...userIds],
    })
  );

  return res.data;
};

type CheckValidReportLinkParams = {
  /** 예약 제출(scheduled_submit) 식별자. 공유 링크 진입 등 값이 없는 경로에서는 빈 문자열로 전송한다. */
  id: string;
  /** 보고서 식별자. `/reports`처럼 경로에 보고서 ID가 없는 진입에서는 빈 문자열로 전송한다. */
  report_id: string;
};

type CheckValidReportLinkResponse = {
  policy?: unknown;
};

/**
 * GET /web/check_valid_link
 */
export const checkValidReportLink = async (params: CheckValidReportLinkParams) => {
  const res = await API.get<CheckValidReportLinkResponse>('/web/check_valid_link', { params });

  return res.data;
};

export const putReport = async (params: PutReportParam) => {
  const res = await API.put<PutReportResponse>('/web/report', {
    id: params.id,
    report: JSON.stringify({
      report_plan_item_id: params.report.report_plan_item_id,
      attach_expense: {
        checked_all: params.report.attachExpense.checkedAll, // 전체선택 유무 optional
        ids: params.report.attachExpense.ids, // checked_all 이 true 인 경우 지출목록에서 exclude 의미로 사용됨
        filter: params.report.attachExpense.filter,
      },
    }),
  });

  return res.data;
};

type PutReportDetailParams = {
  id: number;
  report: Record<string, unknown>;
};

type PutReportDetailResponse = {
  report: Report;
  meta: Partial<ReportMeta>;
};

/**
 * PUT /web/report
 */
export const putReportDetail = async (params: PutReportDetailParams) => {
  const res = await API.put<PutReportDetailResponse>(
    '/web/report',
    HttpQueryUtil.FormData({
      id: params.id,
      report: JSON.stringify(params.report),
    })
  );

  return res.data;
};

type PutReportCustomFieldParams = {
  id: number;
  index: number;
  reportCustomField: string;
};

type PutReportCustomFieldResponse = {
  report: Report;
};

/**
 * PUT /web/report/custom_field
 */
export const putReportCustomField = async (params: PutReportCustomFieldParams) => {
  const res = await API.put<PutReportCustomFieldResponse>(
    '/web/report/custom_field',
    HttpQueryUtil.FormData({
      id: params.id,
      index: params.index,
      report_custom_field: params.reportCustomField,
    })
  );

  return res.data;
};

/**
 * GET /web/reports/aggregation
 */
export const getReportsAggregation = async (params: GetReportsAggregationParam) => {
  const res = await API.get<GetReportsAggregationResponse>('/web/reports/aggregation', { params });

  return res.data;
};

export type PostCompleteTransferResponse = {
  success: boolean;
};

/**
 * POST /v4/reports/:id/complete_transfer
 */
export const postCompleteTransfer = async (id: number) => {
  const res = await API.post<PostCompleteTransferResponse>(`/v4/reports/${id}/complete_transfer`);

  return res.data;
};

type GetReportCustomFieldsFilterDataParams = {
  order: SortOption;
};

type ReportCustomFieldFilterData = {
  field_type: ReportCustomFieldType;
  field_title: string;
};

type GetReportCustomFieldsFilterDataResponse = {
  report_custom_fields: ReportCustomFieldFilterData[];
};

/**
 * GET /v4/report_custom_fields/filter_data
 */
export const getReportCustomFieldsFilterData = async (params: GetReportCustomFieldsFilterDataParams) => {
  const res = await API.get<GetReportCustomFieldsFilterDataResponse>('/v4/report_custom_fields/filter_data', {
    params,
  });

  return res.data;
};

type GetReportCustomFieldsFilterInitialValuesParams = {
  field_title: string;
  field_type: ReportCustomFieldType;
  order: SortOption;
  filter?: { initial_value?: string };
};

type GetReportCustomFieldsFilterInitialValuesResponse = {
  initial_values: string[];
};

/**
 * GET /v4/report_custom_fields/filter/initial_values
 */
export const getReportCustomFieldsFilterInitialValues = async (
  params: GetReportCustomFieldsFilterInitialValuesParams
) => {
  const res = await API.get<GetReportCustomFieldsFilterInitialValuesResponse>(
    '/v4/report_custom_fields/filter/initial_values',
    { params }
  );

  return res.data;
};

export type PostReportsExportErpReserveParams = {
  checked_all: boolean;
  report_ids: number[];
  filter?: string;
};

export type PostReportsExportErpReserveResponse = {
  result: {
    job_id: string;
    success: boolean;
  };
};

/**
 * POST /v4/reports/export/erp_reserve
 */
export const postReportsExportErpReserve = async (params: PostReportsExportErpReserveParams) => {
  const res = await API.post<PostReportsExportErpReserveResponse>('/v4/reports/export/erp_reserve', params);

  return res.data;
};

export type GetReportsJobsResultResponse = {
  job_id: string;
  job_type: 'card_upload' | 'export_report';
  total_reports_count: number;
  success_count: number;
  fails_count: number;
  fails: {
    report_id: number;
    report_title: string;
    message: string;
    detail: string;
  }[];
};

/**
 * GET /v4/reports/jobs/:job_id/result
 */
export const getReportsJobsResult = async (jobId: string) => {
  const res = await API.get<GetReportsJobsResultResponse>(`/v4/reports/jobs/${jobId}/result`);

  return res.data;
};

type PostReportsExportDiffCheckParams = {
  include_ids: number[];
  filter?: Record<string, unknown>;
};

export type PostReportsExportDiffCheckResponse = {
  result: {
    total_count: number;
    first_export_report_count: number;
    different_report_count: number;
    different_report_titles: string[];
    same_report_count: number;
    same_report_titles: string[];
  };
};

/**
 * POST /v4/reports/export/diff_check
 */
export const postReportsExportDiffCheck = async (payload: PostReportsExportDiffCheckParams) => {
  const res = await API.post<PostReportsExportDiffCheckResponse>('/v4/reports/export/diff_check', payload);

  return res.data;
};

type PostReportsExportDiffCheckAllParams = {
  exclude_ids: number[];
  filter?: Record<string, unknown>;
};

/**
 * POST /v4/reports/export/diff_check_all
 */
export const postReportsExportDiffCheckAll = async (payload: PostReportsExportDiffCheckAllParams) => {
  const res = await API.post<PostReportsExportDiffCheckResponse>('/v4/reports/export/diff_check_all', payload);

  return res.data;
};

export type PostReportsDuplicateResponse = {
  result: {
    report_id: number;
  };
};

/**
 * POST /v4/reports/:id/duplicate
 */
export const postReportsDuplicate = async (reportId: number) => {
  const res = await API.post<PostReportsDuplicateResponse>(`/v4/reports/${reportId}/duplicate`);

  return res.data;
};

type PutReportExpensesTransferParams = {
  include_ids: number[];
  report_plan_item_id: number;
};

type PutReportExpensesTransferResponse = {
  result: boolean;
};

/**
 * PUT /v4/reports/:id/expenses/transfer_and_attach
 */
export const putReportExpensesTransfer = async (reportId: number, params: PutReportExpensesTransferParams) => {
  const res = await API.put<PutReportExpensesTransferResponse>(
    `/v4/reports/${reportId}/expenses/transfer_and_attach`,
    params
  );

  return res.data;
};

type ApprovalAdjustmentReplaceRejectReason =
  | ApprovalAdjustmentCandidateReason
  | 'seat_excluded'
  | 'slot_already_satisfied';

type ApprovalAdjustmentExcludeRejectReason = 'satisfied' | 'excluded' | 'single_seat_slot';

type ApprovalAdjustmentSeatError =
  | { seat_id: number; code: 40401; field: 'seat_id'; reason: 'seat_not_found' }
  | {
      seat_id: number;
      code: 42201;
      field: 'replacement_policy_member_id';
      reason: ApprovalAdjustmentReplaceRejectReason;
    }
  | { seat_id: number; code: 42203; field: 'seat_id'; reason: ApprovalAdjustmentExcludeRejectReason }
  | { seat_id: number; code: 42203; field: 'adjustments'; reason: 'undecided_seat' }
  | { seat_id: number; code: 42204; field: 'seat_id'; reason: 'final_slot_completion' };

export type PostReportApprovalAdjustmentsErrorResponse = SpenditErrorResponse<
  | SpenditError<40401, 'seat_id', { reason: 'seat_not_found'; seat_errors: ApprovalAdjustmentSeatError[] }>
  | SpenditError<40901, 'seat_version'>
  | SpenditError<
      42201,
      'replacement_policy_member_id',
      { reason: ApprovalAdjustmentReplaceRejectReason; seat_errors: ApprovalAdjustmentSeatError[] }
    >
  | SpenditError<
      42203,
      'seat_id' | 'adjustments',
      | { reason: ApprovalAdjustmentExcludeRejectReason; seat_errors: ApprovalAdjustmentSeatError[] }
      | { reason: 'undecided_seat'; seat_ids: number[]; seat_errors: ApprovalAdjustmentSeatError[] }
    >
  | SpenditError<42204, 'seat_id', { reason: 'final_slot_completion'; seat_errors: ApprovalAdjustmentSeatError[] }>
>;

type GetReportPlanItemResponseReportPlanItem = {
  id: number;
  attendee_ids: number[];
  category: Category | null;
  is_custom: boolean;
  limitation: string;
  description: string | null;
  currency: LabelValue<number>;
  check_type: LabelValue<string>;
  descriptions: {
    app_only: boolean;
    label: string;
    value: string;
  }[];
};

type GetReportPlanItemResponse = {
  report_plan_item: GetReportPlanItemResponseReportPlanItem;
};

/**
 * GET /web/report_plan_item
 */
export const getReportPlanItem = async (reportPlanItemId: number) => {
  const res = await API.get<GetReportPlanItemResponse>('/web/report_plan_item', {
    params: {
      id: reportPlanItemId,
    },
  });

  return res.data;
};

export type GetReportPlanItemsResponse = {
  plans: { plan: LabelValue<number> & { id: number }; sub_plans: (LabelValue<number> & { id: number })[] }[];
};

/**
 * GET /web/report_plans
 */
export const getReportPlanItems = async (reportId: number) => {
  const res = await API.get<GetReportPlanItemsResponse>('/web/report_plans', {
    params: {
      report_id: reportId,
    },
  });

  return res.data;
};

export type PutReportPlanItemParams = {
  categoryId: number;
  currency: string;
  planCheckType: string;
  memo: string | null;
  budgetPrice: string;
  reportId: number;
  confirmPlanBudgetOverage?: boolean;
};

/**
 * POST /web/report_plan_item/custom
 */
export const postReportPlanItem = async (params: PutReportPlanItemParams) => {
  const res = await API.post('/web/report_plan_item/custom', {
    report_id: params.reportId,
    report_plan_item: {
      report_id: params.reportId,
      description: params.memo,
    },
    fixed_plan_item: {
      category_id: params.categoryId,
      currency: params.currency,
      check_type: params.planCheckType,
      limitation: params.budgetPrice,
    },
  });

  return res.data;
};

/**
 * PUT /web/report_plan_item
 */
export const putReportPlanItem = async (reportPlanItemId: number, params: PutReportPlanItemParams) => {
  const res = await API.put('/web/report_plan_item', {
    id: reportPlanItemId,
    confirm_plan_budget_overage: params.confirmPlanBudgetOverage,
    report_plan_item: {
      report_id: params.reportId,
      description: params.memo,
    },
    fixed_plan_item: {
      category_id: params.categoryId,
      currency: params.currency,
      check_type: params.planCheckType,
      limitation: params.budgetPrice,
    },
  });

  return res.data;
};

/**
 * POST /web/report_plan_items
 */
export const postReportPlanItems = async (reportId: number, planId: number) => {
  const res = await API.post('/web/report_plan_items', {
    report_id: reportId,
    plan_id: planId,
  });

  return res.data;
};

interface PostReportsERPDownloadParams {
  format: 'iu' | 'icube';
}

export interface ReportsERPDownloadError {
  code: string;
  message: string;
  extra_data?: {
    [x: string]: string;
  };
}

export interface PostReportsERPDownloadResponse {
  errors: ReportsERPDownloadError[];
  report: { title: string };
}

/**
 * POST /v4/reports/:id/erp_download
 */
export const postReportsERPDownload = async (reportId: number, params: PostReportsERPDownloadParams) => {
  const res = await API.post<PostReportsERPDownloadResponse>(`/v4/reports/${reportId}/erp_download`, {
    format: params.format,
  });

  return res.data;
};

export interface PostReportsERPReportIdsParams {
  checkedAll: boolean;
  format?: ExportType;
  filter: string | null;
  reportIds: number[];
}

interface PostReportsERPReportIdsResponse {
  report_ids: number[];
}

/**
 * POST /web/reports/erp_report_ids
 */
export const postReportsERPReportIds = async (params: PostReportsERPReportIdsParams) => {
  const res = await API.post<PostReportsERPReportIdsResponse>('/web/reports/erp_report_ids', {
    checked_all: params.checkedAll,
    format: params.format,
    filter: params.filter,
    report_ids: params.reportIds,
  });

  return res.data;
};

export interface PutReportExpensesParams {
  id: number;
  expenseParams: {
    changes: {
      payment?: LabelValue<number>;
      category?: LabelValue<number>;
      tag_ids?: {
        parent_id: number;
        id?: number | null | undefined;
      }[];
      bill_payment_at?: ISO8601DateFormatString;
      description?: string;
    };
    check_ids?: number[];
  };
}

export const putReportExpenses = async ({ id, expenseParams }: PutReportExpensesParams) => {
  const res = await API.put('/web/report/expenses', {
    id,
    expense_params: JSON.stringify(expenseParams),
  });

  return res.data;
};

interface ReportWatcher {
  id: number;
  email: string;
  name: string | null;
  thumbnail_url: string | null;
  report_authority_id: number;
}

interface GetReportWatcherResponse {
  users: ReportWatcher[];
}

/**
 * GET /v4/reports/:id/report_authorities/watcher
 */
export const getReportWatcher = async (reportId: number) => {
  const res = await API.get<GetReportWatcherResponse>(`/v4/reports/${reportId}/report_authorities/watcher`);

  return res.data;
};

interface GetReportAddableWatchersParams extends Partial<PaginationParam> {
  filter?: {
    name?: string;
  };
}

interface GetReportAddableWatchersResponse {
  users: Omit<ReportWatcher, 'report_authority_id'>[];
  meta: {
    total_pages: number;
    total_count: number;
  };
}

/**
 * GET /v4/report_authority/addable_watchers
 * @see /apipie/4/report_authorities/addable_watchers.html
 */
export const getReportAddableWatchers = async (reportId: number, params: GetReportAddableWatchersParams) => {
  const res = await API.get<GetReportAddableWatchersResponse>(`/v4/report_authority/addable_watchers`, {
    params: {
      report_id: reportId,
      filter: {
        name: params.filter?.name,
      },
      page: params.page,
      count_per_page: params.countPerPage,
    },
  });

  return res.data;
};

interface PostReportsErpOverviewParams {
  reportIds: number[];
  checkedAll: boolean;
  format: ExportType;
  page: number;
  filter: string | null;
}

/**
 * POST /web/reports/erp_overview
 */
export const postReportsErpOverview = async (params: PostReportsErpOverviewParams) => {
  const res = await API.post('/web/reports/erp_overview', {
    report_ids: params.reportIds,
    checked_all: params.checkedAll,
    format: params.format,
    page: params.page,
    filter: params.filter,
  });

  return res.data;
};

interface GetCheckReportDenialParams {
  userId: number;
}

interface GetCheckReportDenialResponse {
  owner: boolean;
  is_policy_member: boolean;
}

/**
 * GET /v4/report/${reportId}/check_report_denial
 */
export const getCheckReportDenial = async (reportId: number, params: GetCheckReportDenialParams) => {
  const res = await API.get<GetCheckReportDenialResponse>(`/v4/report/${reportId}/check_report_denial`, {
    params: { user_id: params.userId },
  });

  return res.data;
};

interface PutReportDenyParams {
  id: number;
  comment: string;
  check_ids: number[];
  receiver: {
    id: number;
    report_approver_id?: number;
    user_id: number;
  };
}

interface PutReportDenyResponse {
  result: boolean;
}

/**
 * PUT /v4/report/deny
 */
export const putReportDeny = async (params: PutReportDenyParams) => {
  const res = await API.put<PutReportDenyResponse>('/v4/report/deny', params);

  return res.data;
};

/**
 * POST /web/report/review_reject
 */
export const postReviewReject = async (params: PutReportDenyParams) => {
  const res = await API.post<PutReportDenyResponse>('/web/report/review_reject', params);

  return res.data;
};

interface PutEditorContentParams {
  url: string;
  content: string;
  cancelToken?: CancelToken;
}
/**
 * POST S3 URL에 내용을 업데이트
 * @param params
 */
export const putEditorContent = (params: PutEditorContentParams) => {
  const file = new Blob([params.content], { type: 'text/html' });

  const putRequest = (cancelToken: CancelToken | undefined) => {
    return API.put(params.url, file, {
      headers: {
        'Content-Type': file.type,
      },
      cancelToken,
    }).then(res => res.data);
  };

  return putRequest(params?.cancelToken).catch(error => {
    /**
     * AWS의 S3 url을 이용해 너무 자주 요청을 하는 경우 503 에러가 발생할 수 있음. 이 경우 재시도
     * @see https://repost.aws/ko/knowledge-center/http-5xx-errors-s3
     */
    if (isAWSSlowDownErrorResponse(error.response)) {
      return putRequest(undefined); // 최대 1회 재시도
    }
    throw error;
  });
};

interface GetEditorUploadUrlResponse {
  upload_url: string;
}

/**
 * GET /v4/reports/:id/editor_content/upload_url
 * @param reportId
 */
export const getEditorContentUploadUrl = async (reportId: number) => {
  const res = await API.get<GetEditorUploadUrlResponse>(`/v4/reports/${reportId}/editor_content/upload_url`);

  return res.data;
};

type PostChangeEditorTemplateParams = {
  templateId: number;
};

/**
 * POST /v4/reports/:id/editor_content/change_template
 * @param reportId
 * @param params
 */
export const postChangeEditorTemplate = async (reportId: number, params: PostChangeEditorTemplateParams) => {
  const res = await API.post(`/v4/reports/${reportId}/editor_content/change_template`, {
    report_editor_template_id: params.templateId,
  });

  return res.data;
};

interface PostUploadStatusResponse {
  report: Report;
}

/**
 * POST /v4/reports/:id/editor_content/upload_complete
 * @param reportId
 */
export const postUploadStatus = async (reportId: number) => {
  const res = await API.post<PostUploadStatusResponse>(`/v4/reports/${reportId}/editor_content/upload_complete`);

  return res.data;
};

/**
 * 앱 웹뷰 전용 요청 헤더.
 *
 * 웹뷰 라우트는 `RouteWithoutAuth`로 렌더돼 세션 사가를 타지 않으므로 `setAuthenticationHeader`가
 * 호출되지 않고, `Accept-Language` 또한 axios 인터셉터가 주입하지 않는다. 두 헤더 모두 호출별로 직접 넘긴다.
 */
type WebViewRequestHeaders = {
  Authentication: string | undefined;
  'Accept-Language': string;
};

/**
 * GET /web/report/:id — 앱 웹뷰 보고서 에디터용
 *
 * 웹 화면용 {@link getReport}와 달리 `include_expenses` 쿼리를 보내지 않는다.
 * @param reportId
 * @param headers
 */
export const getWebViewReport = async (reportId: string | null, headers: WebViewRequestHeaders) => {
  const res = await API.get<{ report: Report }>(`/web/report/${reportId}`, { headers });

  return res.data;
};

/**
 * GET /v4/reports/:id/editor_content/upload_url — 앱 웹뷰 보고서 에디터용
 * @param reportId
 * @param headers
 */
export const getWebViewEditorContentUploadUrl = async (reportId: number, headers: WebViewRequestHeaders) => {
  const res = await API.get<GetEditorUploadUrlResponse>(`/v4/reports/${reportId}/editor_content/upload_url`, {
    headers,
  });

  return res.data;
};

/**
 * POST /v4/reports/:id/editor_content/change_template — 앱 웹뷰 보고서 에디터용
 *
 * 레거시(`$.ajax`)와 동일하게 `FormData`로 전송한다.
 * @param reportId
 * @param params
 * @param headers
 */
export const postWebViewChangeEditorTemplate = async (
  reportId: number,
  params: PostChangeEditorTemplateParams,
  headers: WebViewRequestHeaders
) => {
  const res = await API.post(
    `/v4/reports/${reportId}/editor_content/change_template`,
    HttpQueryUtil.FormData({ report_editor_template_id: params.templateId }),
    { headers }
  );

  return res.data;
};

/**
 * POST /v4/reports/:id/editor_content/upload_complete — 앱 웹뷰 보고서 에디터용
 * @param reportId
 * @param headers
 */
export const postWebViewUploadStatus = async (reportId: number, headers: WebViewRequestHeaders) => {
  const res = await API.post<PostUploadStatusResponse>(
    `/v4/reports/${reportId}/editor_content/upload_complete`,
    undefined,
    { headers }
  );

  return res.data;
};

/**
 * url은 서버에서 내려주는 S3 URL
 * @param url
 */
export const getEditorContentFormDownloadURL = async (url: string, cancelToken?: CancelToken) => {
  const res = await API.get(url, { cancelToken });
  const urlWithoutQuery = (res.config.url ?? '').split('?')[0];
  const reportId = Number(urlWithoutQuery.split('/').pop());

  return {
    content: res.data,
    id: reportId,
  };
};

type GetReportsAwaitingApprovalResponse = {
  approval_count: number;
  refund_and_transfer_count: number;
  designated_plan_count: number;
  refund_and_transfer_completed_count: number;
  adjustment_needed_count: number;
};

/**
 * GET /v4/reports/awaiting_approval
 */
export const getReportsAwaitingApproval = async () => {
  const res = await API.get<GetReportsAwaitingApprovalResponse>('/v4/reports/awaiting_approval');

  return res.data;
};

type PostReportsBulkApproveParams = {
  checkedAll?: boolean;
  reportIds?: number[];
  filter?: string;
};

export type ReportBulkApproveResult = {
  id: number;
  report_user_name: string;
  report_user_email: string;
  report_title: string;
  message: string;
  status: boolean;
};

type PostReportsBulkApproveResponse = {
  total_count: number;
  success_count: number;
  result: ReportBulkApproveResult[];
};

const postReportsBulkApproveParamsSerializer = (params: PostReportsBulkApproveParams) => {
  if (params.checkedAll) {
    return { checked_all: true, exclude_ids: params.reportIds, filter: params.filter };
  }

  return { checked_all: false, include_ids: params.reportIds };
};

/**
 * POST /v4/reports/bulk_approve
 */
export const postReportsBulkApprove = async (params: PostReportsBulkApproveParams) => {
  const res = await API.post<PostReportsBulkApproveResponse>(
    '/v4/reports/bulk_approve',
    postReportsBulkApproveParamsSerializer(params)
  );

  return res.data;
};

export type GetReportAuthoritiesAddableWatcherUsersParams = {
  filter: { name: string };
  page: number;
  countPerPage: number;
};

type GetReportAuthoritiesAddableWatcherUsersResponse = {
  meta: {
    total_count: number;
    total_pages: number;
  };
  users: WatchAddableUser[];
};

/**
 * GET /v4/report_authorities/addable_watcher_users
 */
export const getReportAuthoritiesAddableWatcherUsers = async (
  params: GetReportAuthoritiesAddableWatcherUsersParams
) => {
  const res = await API.get<GetReportAuthoritiesAddableWatcherUsersResponse>(
    '/v4/report_authorities/addable_watcher_users',
    {
      params: {
        filter: params.filter,
        page: params.page,
        count_per_page: params.countPerPage,
      },
    }
  );

  return res.data;
};

export type GetReportAuthoritiesAddableWatcherGroupsParams = {
  filter: {
    name: string;
  };
};

type GetReportAuthoritiesAddableWatcherGroupsResponse = {
  groups: WatchAddableGroup[];
};

/**
 * GET /v4/report_authorities/addable_watcher_groups
 */
export const getReportAuthoritiesAddableWatcherGroups = async (
  params: GetReportAuthoritiesAddableWatcherGroupsParams
) => {
  const res = await API.get<GetReportAuthoritiesAddableWatcherGroupsResponse>(
    '/v4/report_authorities/addable_watcher_groups',
    { params }
  );

  return res.data;
};

type GetReportsAuthoritiesWatchersResponse = {
  report_watchers: ReportWatcherV2[];
};

/**
 * GET /v4/reports/:id/report_authorities/watchers
 */
export const getReportsAuthoritiesWatchers = async (reportId: number) => {
  const res = await API.get<GetReportsAuthoritiesWatchersResponse>(
    `/v4/reports/${reportId}/report_authorities/watchers`
  );

  return res.data;
};

export type PutReportAuthoritiesWatchersParams = {
  reportWatchers: { watcher_type: ReportWatcherType; id: number; users?: { id: number }[] }[];
};

type PutReportAuthoritiesWatchersResponse = {
  watchers: ReportWatcherV2[];
};

/**
 * PUT /v4/reports/:id/report_authorities/watchers
 */
export const putReportAuthoritiesWatchers = async (reportId: number, params: PutReportAuthoritiesWatchersParams) => {
  const res = await API.put<PutReportAuthoritiesWatchersResponse>(
    `/v4/reports/${reportId}/report_authorities/watchers`,
    {
      report_watchers: params.reportWatchers,
    }
  );

  return res.data;
};

type GetReportAuthoritiesAddableWatcherRecentResponse = {
  recent_watchers: RecentWatcher[];
};

/**
 * GET /v4/report_authorities/addable_watcher_recent
 */
export const getReportAuthoritiesAddableWatcherRecent = async () => {
  const res = await API.get<GetReportAuthoritiesAddableWatcherRecentResponse>(
    '/v4/report_authorities/addable_watcher_recent'
  );

  return res.data;
};

export type PostReportsBulkTransferAndRefundParams = {
  checkedAll: boolean;
  reportIds: number[];
  filter: string;
};

type PostReportsBulkTransferAndRefundResponse = {
  total_count: number;
  success_count: number;
  result: ReportBulkTransferAndRefundResult[];
};

const postReportsBulkTransferAndRefundParamsSerializer = (params: PostReportsBulkTransferAndRefundParams) => {
  if (params.checkedAll) {
    return { checked_all: true, exclude_ids: params.reportIds, filter: params.filter };
  }

  return { checked_all: false, include_ids: params.reportIds };
};

/**
 * POST /v4/reports/bulk_transfer_and_refund
 */
export const postReportsBulkTransferAndRefund = async (params: PostReportsBulkTransferAndRefundParams) => {
  const res = await API.post<PostReportsBulkTransferAndRefundResponse>(
    '/v4/reports/bulk_transfer_and_refund',
    postReportsBulkTransferAndRefundParamsSerializer(params)
  );

  return res.data;
};

export type PostWriterRole = 'normal' | 'editor' | 'policy_approver' | 'hidden' | 'plan_approver' | 'auditor' | null;

export type PostWriter = {
  id: number;
  name: string;
  email: string;
  thumbnail_url: string;
  role: PostWriterRole;
  group_id: number | null; // 소속 policy 연동 그룹 ID (1인 1그룹). 미소속이면 null
  group_name: string | null; // 소속 policy 연동 그룹 이름. 미소속이면 null
  is_company_admin: boolean;
  is_policy_owner: boolean;
  is_pre_plan_writer: boolean;
  is_plan_approval_line_member: boolean;
  has_post_plan_report: boolean;
};

export type GetReportPostWritersParams = {
  name?: string;
  group_id?: number;
};

export type GetReportPostWritersResponse = {
  post_writers: PostWriter[];
};

/**
 * GET /v4/reports/:id/post_writers
 */
export const getReportPostWriters = async (reportId: number, params?: GetReportPostWritersParams) => {
  const res = await API.get<GetReportPostWritersResponse>(`/v4/reports/${reportId}/post_writers`, { params });

  return res.data;
};

type PostReportPostWritersParams = {
  user_ids: number[];
};

/**
 * POST /v4/reports/:id/post_writers
 */
export const postReportPostWriters = async (reportId: number, params: PostReportPostWritersParams) => {
  const res = await API.post<GetReportPostWritersResponse>(`/v4/reports/${reportId}/post_writers`, params);

  return res.data;
};

export type PostWriterCandidateGroup = {
  id: number;
  name: string;
  member_count: number; // 그룹 내 live 멤버 수
};

/**
 * 지정 드로워(picker) 후보 멤버.
 * 기존 Inner::V4::PolicyMembersSerializer shape — 승인라인 picker(addible_members)와 동일.
 */
export type PostWriterCandidate = {
  id: number; // PolicyMember id
  user_id: number;
  name: string;
  email: string;
  thumbnail_url: string | null;
  role: PostWriterRole;
  group_id: number | null;
  group_name: string | null;
  is_company_admin: boolean;
  is_policy_owner: boolean;
  is_pre_plan_writer: boolean;
  is_plan_approval_line_member: boolean;
  has_post_plan_report: boolean;
};

export type GetReportPostWriterCandidatesParams = {
  /** 숫자 → 해당 그룹 멤버만 / 'none' → 그룹 미지정 멤버만 / 미지정 → 전체 후보. members 에만 적용 */
  group_id?: number | 'none';
  /** 이름/이메일 부분 일치 (case-insensitive). members 필터 */
  name?: string;
};

export type GetReportPostWriterCandidatesResponse = {
  groups: PostWriterCandidateGroup[]; // policy 연동 그룹 목록 (좌측 컬럼)
  ungrouped_member_count: number; // "그룹 미지정" 버킷 멤버 수 (?group_id=none 으로 조회)
  members: PostWriterCandidate[]; // 후보 멤버 (필터 적용, 페이지네이션 없음)
};

/**
 * GET /v4/reports/:id/post_writer_candidates
 */
export const getReportPostWriterCandidates = async (reportId: number, params?: GetReportPostWriterCandidatesParams) => {
  const res = await API.get<GetReportPostWriterCandidatesResponse>(`/v4/reports/${reportId}/post_writer_candidates`, {
    params,
  });

  return res.data;
};

type ApprovalAdjustmentUser = {
  id: number;
  name: string;
  email: string;
  thumbnail_url: string | null;
};

type ApprovalAdjustmentCandidateReason = 'not_policy_member' | 'inactive' | 'no_approver_permission' | 'submitter_self';

type ApprovalAdjustmentCandidate = {
  policy_member_id: number;
  role: ApproverFlowPolicyRole;
  kind: 'member' | 'company_admin' | 'company_owner';
  is_policy_owner: boolean;
  user: ApprovalAdjustmentUser;
  same_line_limit_price: string | null;
};

export type GetReportApprovalAdjustmentsResponse = {
  seat_version: string;
  steps_until_vacancy: number | null;
  approval_line_mode: ReportSubmitType | null;
  limit_type: 'by_report' | 'by_expense' | null;
  slots: {
    slot_position: number;
    role: 'approver' | 'reviewer' | 'final_approver';
    status: 'stalled' | 'upcoming' | 'satisfied';
    seats: {
      seat_id: number;
      user: ApprovalAdjustmentUser | null;
      replaceable: boolean;
      excludable: boolean;
      limit_price: string | null;
    }[];
  }[];
  candidates: Record<'approver' | 'final_approver', ApprovalAdjustmentCandidate[]>;
};

/**
 * GET /v4/reports/:id/approval_adjustments
 */
export const getReportApprovalAdjustments = async (reportId: number) => {
  const res = await API.get<GetReportApprovalAdjustmentsResponse>(`/v4/reports/${reportId}/approval_adjustments`);

  return res.data;
};

export type ReportApprovalSeatAdjustment =
  | { seat_id: number; action: 'replace'; replacement_policy_member_id: number }
  | { seat_id: number; action: 'exclude' };

export type PostReportApprovalAdjustmentsParams =
  | { seat_version: string; adjustments: ReportApprovalSeatAdjustment[] }
  | { seat_version: string; adjustment_action: 'reset' };

type PostReportApprovalAdjustmentsResponse = {
  result: true;
  adjustment: GetReportApprovalAdjustmentsResponse;
};

/**
 * POST /v4/reports/:id/approval_adjustments
 */
export const postReportApprovalAdjustments = async (reportId: number, params: PostReportApprovalAdjustmentsParams) => {
  const res = await API.post<PostReportApprovalAdjustmentsResponse>(
    `/v4/reports/${reportId}/approval_adjustments`,
    params
  );

  return res.data;
};
